import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AuthModal } from './auth/AuthModal';
import { 
  Box, 
  Shield, 
  Users, 
  AlertCircle,
  ChevronRight,
  Building2,
  GitBranch,
  GraduationCap,
  AlertTriangle,
  CalendarClock
} from 'lucide-react';

export function WelcomeScreen() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/sstxximv_Zentara%20As-Ltd._-01.png" 
            alt="Zentara" 
            className="h-10 w-auto object-contain"
          />
          <div>
            <h1 className="text-lg font-semibold text-foreground leading-tight zentara-header">
              Zentara
            </h1>
            <p className="text-[10px] text-muted-foreground">
              Industrial & Commercial Asset Management
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="border-primary/30 text-primary hover:bg-primary/10"
          onClick={() => setIsAuthModalOpen(true)}
        >
          Sign In
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-5xl w-full text-center">
          {/* Zentara Logo */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_industrial-3d/artifacts/6d9nrv5o_Zentara-As-Ltd._CROP.png"
              alt="Zentara As-Built & Engineering Group Ltd"
              className="h-32 sm:h-40 mx-auto"
            />
          </div>

          {/* Hero Section */}
          <Badge variant="outline" className="mb-6 text-xs border-primary/30 text-primary">
            Beyond the Interactive Model
          </Badge>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 zentara-header">
            Your Facility's <span className="text-primary">Shared Operational Truth</span>
          </h2>
          
          <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto">
            Most 3D scans end up as interactive models — useful for design, occasionally opened, then quietly forgotten.
          </p>
          
          <p className="text-lg text-foreground mb-8 max-w-3xl mx-auto">
            We're evolving the 360° environment into something more: <span className="text-primary">a space that shows what's happening, what's planned, what's at risk, and who's qualified to do the work.</span>
          </p>

          <Button 
            size="lg" 
            className="gap-2 mb-12"
            onClick={() => setIsAuthModalOpen(true)}
          >
            Enter Your Facility
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Value Proposition */}
          <div className="bg-card/30 border border-border rounded-2xl p-6 mb-12 max-w-3xl mx-auto">
            <p className="text-sm text-muted-foreground italic">
              "Inside the model, the facility tells its own story — fewer surprises, less tribal knowledge, faster alignment, and real ROI from the scan itself."
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon={<AlertCircle className="h-5 w-5" />}
              title="Active Issues"
              description="Visible where they occur"
            />
            <FeatureCard
              icon={<Building2 className="h-5 w-5" />}
              title="Assets"
              description="History, ownership & cost"
            />
            <FeatureCard
              icon={<GitBranch className="h-5 w-5" />}
              title="Workflows"
              description="Tied to physical space"
            />
            <FeatureCard
              icon={<GraduationCap className="h-5 w-5" />}
              title="Competency"
              description="In-context training & validation"
            />
            <FeatureCard
              icon={<Shield className="h-5 w-5" />}
              title="Compliance"
              description="Obvious, not buried"
            />
            <FeatureCard
              icon={<CalendarClock className="h-5 w-5" />}
              title="Planned Changes"
              description="Surfaced before surprises"
            />
            <FeatureCard
              icon={<AlertTriangle className="h-5 w-5" />}
              title="Risk & Exposure"
              description="Made visible, not assumed"
            />
            <FeatureCard
              icon={<Users className="h-5 w-5" />}
              title="Team Access"
              description="Right people, right permissions"
            />
          </div>

          {/* Footer Note */}
          <p className="mt-10 text-sm text-muted-foreground">
            Sign in to access your facility's shared operational truth.
          </p>
        </div>
      </main>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card/50 text-left hover:border-primary/30 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          {icon}
        </div>
        <h3 className="font-semibold text-foreground text-sm">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground pl-12">{description}</p>
    </div>
  );
}

export default WelcomeScreen;
